import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Rate, Card, Row, Col, Typography, Progress, Avatar, Empty, Spin, Tag, Divider, List, Pagination } from 'antd';
import { StarFilled, UserOutlined } from '@ant-design/icons';
import { reviewActions } from '../actions/reviewActions';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

interface Props {
  instructorId: string;
}

const StarBreakdownBar: React.FC<{ star: number; count: number; total: number }> = ({ star, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <Text style={{ minWidth: 30, fontSize: 12, color: '#64748b' }}>{star}★</Text>
      <Progress
        percent={pct}
        showInfo={false}
        strokeColor={star >= 4 ? '#22c55e' : star === 3 ? '#f59e0b' : '#ef4444'}
        trailColor="#f1f5f9"
        style={{ flex: 1, margin: 0 }}
        size="small"
      />
      <Text style={{ minWidth: 24, fontSize: 12, color: '#64748b', textAlign: 'right' }}>{count}</Text>
    </div>
  );
};

const InstructorRatingsTab: React.FC<Props> = ({ instructorId }) => {
  const dispatch = useDispatch();
  const ratingsState = useSelector((state: any) => state.getInstructorRatingsReducer);
  const [currentPage, setCurrentPage] = React.useState(1);
  const pageSize = 5;

  useEffect(() => {
    if (instructorId) {
      dispatch(reviewActions.getInstructorRatingsRequest(instructorId));
    }
  }, [instructorId, dispatch]);

  const isLoading = Boolean(ratingsState?.isLoading);
  const data = ratingsState?.data;

  // Detect if admin response (has summary + data[]) or instructor response (flat summary)
  const isAdminView = data && Array.isArray(data?.data);
  const summary = isAdminView ? data?.summary : data;
  const reviewList: any[] = isAdminView ? (data?.data || []) : [];
  const totalCount: number = isAdminView ? (data?.totalCount || 0) : 0;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  const avg = summary?.averageRating ?? 0;
  const total = summary?.totalReviews ?? 0;
  const starBreakdown: Record<number, number> = summary?.starBreakdown ?? { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

  const getRatingColor = (r: number) => r >= 4 ? '#22c55e' : r >= 3 ? '#f59e0b' : '#ef4444';
  const getRatingLabel = (r: number) => r >= 4.5 ? 'Excellent' : r >= 4 ? 'Very Good' : r >= 3 ? 'Good' : r >= 2 ? 'Fair' : 'Poor';

  const paginatedReviews = reviewList.slice((currentPage - 1) * pageSize, currentPage * pageSize);

 
  return (
    <div style={{ padding: '4px 0' }}>
      {total === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">No ratings yet</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>Students who complete sessions will be able to leave ratings</Text>
            </div>
          }
          style={{ margin: '48px 0' }}
        />
      ) : (
        <Row gutter={[20, 20]}>
          {/* Overall Score Card */}
          <Col xs={24} md={10}>
            <Card className="instructor-score-card" bodyStyle={{ padding: '32px 24px' }}>
              {/* Header label */}
              <Text className="instructor-score-label">Overall Rating</Text>

              {/* Big number */}
              <div className="instructor-score-avg-wrap">
                <span className={`instructor-score-num ${avg >= 4 ? 'score-good' : avg >= 3 ? 'score-medium' : 'score-poor'}`}>
                  {avg.toFixed(1)}
                </span>
                <span className="instructor-score-out-of">/ 5</span>
              </div>

              {/* Stars */}
              <Rate
                disabled
                value={avg}
                allowHalf
                className="instructor-score-stars"
              />

              {/* Label badge */}
              <div className="instructor-score-badge-wrap">
                <span className={`instructor-score-badge ${avg >= 4 ? 'badge-good' : avg >= 3 ? 'badge-medium' : 'badge-poor'}`}>
                  {getRatingLabel(avg)}
                </span>
              </div>

              {/* Divider */}
              <div className="instructor-score-divider" />

              {/* Review count */}
              <Text className="instructor-score-review-count">
                Based on <strong>{total}</strong> {total === 1 ? 'review' : 'reviews'}
              </Text>
            </Card>
          </Col>

          {/* Star Breakdown */}
          <Col xs={24} md={14}>
            <Card
              style={{ borderRadius: 16, border: '1px solid #e2e8f0', height: '100%' }}
              bodyStyle={{ padding: '20px 24px' }}
            >
              <Text strong style={{ fontSize: 14, color: '#374151', display: 'block', marginBottom: 16 }}>
                Rating Breakdown
              </Text>
              {[5, 4, 3, 2, 1].map((star) => (
                <StarBreakdownBar key={star} star={star} count={starBreakdown[star] ?? 0} total={total} />
              ))}
              <Divider style={{ margin: '14px 0' }} />
              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: getRatingColor(avg) }}>{avg.toFixed(1)}</div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Average</Text>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#374151' }}>{total}</div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Total Reviews</Text>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#8b5cf6' }}>
                    {total > 0 ? Math.round(((starBreakdown[4] ?? 0) + (starBreakdown[5] ?? 0)) / total * 100) : 0}%
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Positive</Text>
                </div>
              </div>
            </Card>
          </Col>

          {/* Review Comments List — Admin only */}
          {isAdminView && reviewList.length > 0 && (
            <Col xs={24}>
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text strong style={{ fontSize: 15, color: '#1e293b' }}>Student Reviews</Text>
                    <Tag style={{ borderRadius: 20, fontSize: 12, background: '#f1f5f9', border: 'none', color: '#64748b' }}>
                      {totalCount} {totalCount === 1 ? 'comment' : 'comments'}
                    </Tag>
                  </div>
                }
                style={{ borderRadius: 16, border: '1px solid #e2e8f0' }}
                bodyStyle={{ padding: '8px 24px 20px' }}
              >
                <List
                  dataSource={paginatedReviews}
                  renderItem={(review: any, index: number) => (
                    <List.Item style={{ padding: '18px 0', border: 'none', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', gap: 12, width: '100%' }}>
                        {/* Avatar */}
                        <Avatar
                          size={38}
                          icon={<UserOutlined />}
                          style={{
                            background: `hsl(${(review.studentName?.charCodeAt(0) || 65) * 5 % 360}, 60%, 65%)`,
                            color: '#fff',
                            flexShrink: 0,
                            fontWeight: 700,
                            fontSize: 15,
                          }}
                        >
                          {review.studentName?.charAt(0)?.toUpperCase() || 'A'}
                        </Avatar>

                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Name + Date row */}
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                            <Text strong style={{ fontSize: 13, color: '#0f172a' }}>
                              {review.studentName || 'Anonymous'}
                            </Text>
                            {review.studentEmail && (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                · {review.studentEmail}
                              </Text>
                            )}
                            <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>
                              {dayjs(review.createdAt).format('MMM D, YYYY')}
                            </Text>
                          </div>

                          {/* Star rating */}
                          <Rate
                            disabled
                            value={review.rating}
                            allowHalf
                            style={{ fontSize: 12, color: '#fbbf24', marginBottom: 6 }}
                          />

                          {/* Comment text */}
                          {review.comment ? (
                            <Text style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.65, display: 'block' }}>
                              {review.comment}
                            </Text>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                              No comment provided.
                            </Text>
                          )}
                        </div>
                      </div>

                      {/* Separator — skip last item */}
                      {index < paginatedReviews.length - 1 && (
                        <Divider style={{ margin: 0, position: 'absolute', bottom: 0, left: 0, right: 0 }} />
                      )}
                    </List.Item>
                  )}
                />

                {totalCount > pageSize && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={totalCount}
                      onChange={(page) => { setCurrentPage(page); }}
                      size="small"
                      showTotal={(t, range) => `${range[0]}–${range[1]} of ${t} reviews`}
                    />
                  </div>
                )}
              </Card>
            </Col>
          )}
        </Row>
      )}
    </div>
  );
};

export default InstructorRatingsTab;